import React from "react";
import { parse, isValid, format } from "date-fns";
import { AccountingAccount } from "../services/accounting-account-service";

interface Props {
  data: AccountingAccount[];
  loading?: boolean;
  isInactive?: boolean;
  onEdit?: (item: AccountingAccount) => void;
  onView?: (item: AccountingAccount) => void;
  onChangeStatus?: (item: AccountingAccount, newStatus: number) => void;
}

export const AccountingAccountTable: React.FC<Props> = ({
  data,
  loading,
  isInactive = false,
  onEdit,
  onView,
  onChangeStatus,
}) => {
  const renderType = (item: AccountingAccount) =>
    item.parent_id && item.parent_id > 0
      ? "Tài khoản chi tiết"
      : "Tài khoản tổng hợp";
  const renderDateTime = (value?: string) => {
    if (!value) return "—";
    let d = new Date(value);
    if (isNaN(d.getTime())) {
      const p = parse(value, "dd/MM/yyyy HH:mm", new Date());
      if (isValid(p)) d = p; else return value;
    }
    return format(d, "dd/MM/yyyy HH:mm");
  };

  return (
    <div className="rounded-xl overflow-hidden shadow-sm bg-white border border-gray-300">
      <table className="w-full text-sm text-gray-900 border-collapse border-1 border-gray-300">
        {/* Header */}
        <thead className="bg-gray-100 text-gray-800 font-semibold border-b border-gray-300 whitespace-nowrap">
          <tr>
            <th className="py-3 px-4 text-center border-r border-gray-300">
              Mã tài khoản
            </th>
            <th className="py-3 px-4 text-center border-r border-gray-300">
              Tên tài khoản
            </th>
            <th className="py-3 px-4 text-center border-r border-gray-300">
              Mô tả
            </th>
            <th className="py-3 px-4 text-center border-r border-gray-300">
              Dư Có
            </th>
            <th className="py-3 px-4 text-center border-r border-gray-300">
              Dư Nợ
            </th>
            <th className="py-3 px-4 text-center border-r border-gray-300">
              Ngày tạo
            </th>
            <th className="py-3 px-4 text-center border-r border-gray-300">
              Loại tài khoản
            </th>
            <th className="py-3 px-4 text-center">Hành động</th>
          </tr>
        </thead>

        {/* Body */}
        <tbody className="bg-white divide-y divide-gray-200">
          {loading ? (
            <tr>
              <td colSpan={8} className="py-6 text-center text-gray-500 italic">
                Đang tải dữ liệu...
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={8} className="py-6 text-center text-gray-500 italic">
                Không có dữ liệu
              </td>
            </tr>
          ) : (
            data.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-green-50 transition border-t border-gray-200 group"
              >
                <td className="py-2.5 px-4 border-r border-gray-200 font-medium text-gray-900">
                  {item.code}
                </td>
                <td className="py-2.5 px-4 border-r border-gray-200">
                  {item.name}
                </td>
                <td className="py-2.5 px-4 border-r border-gray-200 text-gray-700">
                  {item.description || "—"}
                </td>
                <td className="py-2.5 px-4 border-r border-gray-200 text-right text-gray-800">
                  {Number(item.credit ?? 0).toLocaleString()}
                </td>
                <td className="py-2.5 px-4 border-r border-gray-200 text-right text-gray-800">
                  {Number(item.debit ?? 0).toLocaleString()}
                </td>
                <td className="py-2.5 px-4 border-r border-gray-200 text-gray-700">{renderDateTime(item.created_at)}</td>
                <td className="py-2.5 px-4 border-r border-gray-200 text-gray-800">
                  {renderType(item)}
                </td>
                <td className="py-2.5 px-4 text-center">
                  <div className="flex justify-center gap-2">
                    {/* Toggle Status */}
                    <button
                      onClick={() =>
                        onChangeStatus?.(item, isInactive ? 1 : 0)
                      }
                      className={`group relative grid h-9 w-9 place-items-center rounded-full text-white shadow-sm transition ${
                        isInactive
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      {isInactive ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.5 12.75l6 6 9-13.5"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="h-4 w-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 5.25v13.5M15 5.25v13.5"
                          />
                        </svg>
                      )}
                      <span className="pointer-events-none absolute bottom-11 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover:block">
                        {isInactive ? "Kích hoạt" : "Tạm ngưng"}
                      </span>
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => onEdit?.(item)}
                      className="group relative grid h-9 w-9 place-items-center rounded-full bg-yellow-500 text-white shadow-sm transition hover:bg-yellow-600"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-4 w-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652l-9.547 9.546a4.5 4.5 0 01-1.897 1.13l-3.034.909.91-3.034a4.5 4.5 0 011.129-1.897l9.547-9.546z"
                        />
                      </svg>
                      <span className="pointer-events-none absolute bottom-11 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover:block">
                        Sửa
                      </span>
                    </button>

                    {/* View */}
                    <button
                      onClick={() => onView?.(item)}
                      className="group relative grid h-9 w-9 place-items-center rounded-full bg-gray-200 text-gray-700 shadow-sm transition hover:bg-gray-300"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-4 w-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="pointer-events-none absolute bottom-11 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-xs text-white group-hover:block">
                        Xem
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
